import { normalizeMenuPath } from "../layout/menu";
import type { MenuRoute } from "../layout/menu";

/**
 * Persönliche Pins in der Titelleiste.
 *
 * Bewusst schlank gehalten: Icon und Beschriftung von Routen-Pins werden NICHT gespeichert,
 * sondern zur Renderzeit aus `buildMenu(...)` aufgelöst. Dadurch bleiben Pins bei
 * Sprachwechsel, umbenannten Menüpunkten und Rechteänderungen automatisch korrekt —
 * und ein Pin auf eine nicht mehr sichtbare Route ist an der fehlenden Menü-Route erkennbar.
 */

/**
 * Rein technische Obergrenze, kein Bedienlimit: wie viele Pins sinnvoll sind, regelt der
 * verfügbare Platz in der Titelleiste — was nicht hinpasst, sammelt das Überlauf-Menü.
 * Der Riegel verhindert nur, dass die UI-Preferences ins serverseitige Größenlimit laufen
 * (`PUT /api/v1/auth/ui-preferences` lehnt über 100.000 Zeichen ab).
 */
export const MAX_PINNED_LINKS = 50;

export type PinnedLinkKind = "person" | "route";

export type PinnedLink = {
  /** Stabiler Schlüssel: "person:{guid}" | "route:{normalizedPath}". */
  id: string;
  kind: PinnedLinkKind;
  /** Interne Router-Route, z. B. "/persons/{id}". */
  path: string;
  /** Nur für Personen-Pins (Name). Routen-Pins holen ihre Beschriftung aus dem Menü. */
  label?: string;
  /** i18n-Key für einen Tooltip-Zusatz, z. B. der Lifecycle-Status einer Person. */
  subtitleKey?: string;
  /** GUID bei kind === "person". */
  targetId?: string;
  /** Nur Personen-Pins: spart den Foto-Request für Personen ohne hinterlegtes Bild. */
  hasPhoto?: boolean;
};

export function personPinId(personId: string): string {
  return `person:${personId}`;
}

export function routePinId(path: string): string {
  return `route:${normalizeMenuPath(path)}`;
}

export function buildRoutePin(route: MenuRoute): PinnedLink {
  const path = normalizeMenuPath(route.path);
  return { id: routePinId(path), kind: "route", path };
}

export function buildPersonPin(args: {
  personId: string;
  label: string;
  subtitleKey?: string | null;
  hasPhoto?: boolean | null;
}): PinnedLink {
  const path = `/persons/${args.personId}`;
  return {
    id: personPinId(args.personId),
    kind: "person",
    path,
    label: args.label,
    ...(args.subtitleKey ? { subtitleKey: args.subtitleKey } : {}),
    targetId: args.personId,
    ...(args.hasPhoto ? { hasPhoto: true } : {})
  };
}

/** Initialen für den Avatar eines Personen-Pins ("Erika Mustermann" → "EM"). */
export function pinInitials(label: string | null | undefined): string {
  const parts = (label ?? "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  const first = parts[0]?.[0] ?? "";
  const last = parts.length > 1 ? parts[parts.length - 1]?.[0] ?? "" : "";
  return `${first}${last}`.toUpperCase() || "?";
}
